// /security — the training position, data handling per deployment mode, access,
// certification status and the provider record, written for a security reviewer.
import React from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { SitePage, Btn, Go, TO, asset } from './SiteChrome';

const BLOCKS = [
  { tint: '#FBE7DC', title: 'Data handling, per deployment mode.', rows: [
    { k: 'Managed', v: 'Platform data is processed in the environment LatentForce operates. Inference leaves only where you permit an approved third-party model.' },
    { k: 'On-premises', v: 'Platform data and repository content stay in your environment. Permitted external inference is the only egress, under that provider’s terms.' },
    { k: 'Air-gapped', v: 'No external model calls occur in this configuration. Data and inference stay inside the isolated environment.' },
  ] },
  { tint: '#FBF1CC', title: 'Who can see what.', rows: [
    { k: 'Team access', v: 'Access and model tiers are configured per team. Team permissions are distinct from task routing.' },
    { k: 'Repository access', v: 'Repository context respects the access rules of the code host it was indexed from.' },
    { k: 'Human review', v: 'Guidance and skill updates are proposals. A named reviewer approves them before they apply.' },
  ] },
  { tint: '#E4E7DC', title: 'Certification status, stated truthfully.', rows: [
    { k: 'Certifications held', v: 'LatentForce does not currently publish a SOC 2 or ISO/IEC 42001 certification. Where a certification is not held, this page says so rather than implying it.' },
    { k: 'Security questionnaire', v: 'Completed on request for an evaluation in progress, against the configuration under discussion.' },
    { k: 'Contractual commitments', v: 'Data handling commitments are in the Terms of service and the Privacy policy, not on this page alone.' },
  ] },
  { tint: '#F4B9A0', title: 'A provider record per deployment.', rows: [
    { k: 'Approach', v: 'No single fixed subprocessor list describes every managed, on-premises and air-gapped configuration. The provider record is produced per deployment.' },
    { k: 'Model providers', v: 'Any approved third-party model you permit is a provider for that workload, under that provider’s terms.' },
    { k: 'Audit trail', v: 'Each request through the gateway is priced, logged and attributed by user, project, model and provider. Requests that do not pass through the gateway are not recorded there.' },
  ] },
];

function SecurityPage() {
  return (
    <SitePage>
      <section className="lsl-wrap lsl-page-hero">
        <div>
          <h1 className="lsl-page-h1">What we do with your code, stated per deployment.</h1>
          <p className="lsl-page-lede">
            Written for a security reviewer. It states the training position, the data handling per deployment mode, the
            provider record and the certifications we do not hold.
          </p>
          <div className="lsl-page-actions">
            <Btn to={TO.deployment} iconRight={ArrowRight}>Deployment and control</Btn>
          </div>
        </div>
        <div className="lsl-art-frame" style={{ background: '#F4B9A0' }}>
          <div aria-hidden style={{ backgroundImage: `url("${asset('/landing/security-hero.svg')}")` }} />
        </div>
      </section>

      <section className="lsl-wrap lsl-sec lsl-sec--tall lsl-stack">
        <div className="lsl-banner">
          <div className="lsl-banner-icon"><ShieldCheck size={40} aria-hidden /></div>
          <div>
            <h2>No training on Customer Content.</h2>
            <p>
              Trace learning produces proposed updates to shared skills and repository instructions, subject to human
              review. No model weights are trained on your content. This wording matches the Terms of service and the
              Privacy policy.
            </p>
          </div>
        </div>
        <div className="lsl-sec-grid">
          {BLOCKS.map((b) => (
            <div key={b.title} className="lsl-tcard">
              <div className="lsl-tcard-head" style={{ background: b.tint }}>
                <h2>{b.title}</h2>
              </div>
              <div className="lsl-kvs">
                {b.rows.map((r) => (
                  <div key={r.k} className="lsl-kv">
                    <span className="lsl-eyebrow">{r.k}</span>
                    <span>{r.v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="lsl-band">
        <div className="lsl-wrap lsl-chip-row">
          <Go to={TO.deployment}>Deployment and control</Go>
          <Go to={TO.gateway}>LatentGateway access controls</Go>
          <Go to={TO.contact}>Contact the team</Go>
        </div>
      </section>
    </SitePage>
  );
}

export default SecurityPage;
